import { Component, Inject } from '@angular/core';
import {
  MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA,
  MatLegacyDialog as MatDialog,
  MatLegacyDialogRef as MatDialogRef,
} from '@angular/material/legacy-dialog';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { TeamActionMockTeamsStore } from '../../team-action-mock-teams.store';
import {
  ALL_PEOPLE,
  establishmentNames,
  memberIssue,
  MOCK_SUPPORTS,
  MockPerson,
  MockSupportEstablishment,
  MockTeam,
  TEAM_ROLES,
  TeamDraft,
  TeamRules,
} from '../../team-action-mock.data';

export interface TeamFormData {
  /** Équipe à modifier, absente en création. */
  team?: MockTeam;
  /** Établissement support imposé (création depuis sa fiche). */
  support?: MockSupportEstablishment;
}

interface Candidate {
  person: MockPerson;
  establishments: string;
  issue: string | null;
}

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Ouvre la modale et enregistre l'équipe dans la session à la validation. */
export function openTeamForm(
  dialog: MatDialog,
  store: TeamActionMockTeamsStore,
  data: TeamFormData,
): Observable<MockTeam | undefined> {
  return dialog
    .open(TeamActionMockTeamFormModalComponent, { width: '720px', data })
    .afterClosed()
    .pipe(map((draft?: TeamDraft) => (draft ? store.save(draft, data.team) : undefined)));
}

// Maquette d'estimation « action d'équipe » : création ou modification d'une équipe, périmètre déduit et éligibilité en direct.
@Component({
  selector: 'team-action-mock-team-form-modal',
  templateUrl: './team-action-mock-team-form-modal.component.html',
})
export class TeamActionMockTeamFormModalComponent {
  readonly roles = TEAM_ROLES;
  readonly supports = MOCK_SUPPORTS;
  readonly editing: boolean;
  readonly supportLocked: boolean;

  name: string;
  email: string;
  role: string | null;
  support: MockSupportEstablishment | null;
  memberIds: string[];

  candidates: Candidate[] = [];
  warnings: string[] = [];

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: TeamFormData,
    private dialogRef: MatDialogRef<TeamActionMockTeamFormModalComponent, TeamDraft>,
  ) {
    const team = data.team;
    this.editing = !!team;
    this.supportLocked = !team && !!data.support;
    this.name = team?.name ?? '';
    this.email = team?.email ?? '';
    this.role = team?.role ?? null;
    this.support = team?.support ?? data.support ?? null;
    this.memberIds = team?.members.map(member => member.id) ?? [];
    this.refresh();
  }

  /** Rôle et établissement support choisis : les règles d'éligibilité s'appliquent. */
  get rules(): TeamRules | null {
    return this.role && this.support ? { role: this.role, support: this.support } : null;
  }

  get perimeter(): string {
    return this.support ? establishmentNames(this.support.clients) : '';
  }

  get selectedNames(): string {
    return this.selected.map(member => member.fullName).join(', ');
  }

  get valid(): boolean {
    return !!this.name.trim() && EMAIL_PATTERN.test(this.email) && !!this.rules;
  }

  private get selected(): MockPerson[] {
    return ALL_PEOPLE.filter(person => this.memberIds.includes(person.id));
  }

  /** Recalcule les candidats et les avertissements après un changement de rôle, de support ou de membres. */
  refresh(): void {
    const rules = this.rules;
    this.candidates = ALL_PEOPLE.map(person => ({
      person,
      establishments: establishmentNames(person.establishments),
      issue: rules ? memberIssue(person, rules) : null,
    })).sort((a, b) => Number(a.issue !== null) - Number(b.issue !== null));
    this.warnings = this.buildWarnings(rules);
  }

  isLocked(candidate: Candidate): boolean {
    return !!candidate.issue && !this.memberIds.includes(candidate.person.id);
  }

  save(): void {
    if (!this.valid || !this.role || !this.support) {
      return;
    }
    this.dialogRef.close({
      name: this.name.trim(),
      email: this.email.trim(),
      role: this.role,
      support: this.support,
      members: this.selected,
    });
  }

  close(): void {
    this.dialogRef.close();
  }

  private buildWarnings(rules: TeamRules | null): string[] {
    if (!rules) {
      return [];
    }
    const warnings = this.selected
      .map(member => ({ member, issue: memberIssue(member, rules) }))
      .filter(item => item.issue)
      .map(item => `${item.member.fullName} : ${item.issue}. Le membre reste dans l’équipe, signalé.`);
    const team = this.data.team;
    if (team) {
      if (team.role !== rules.role && team.usages.length) {
        warnings.push(`Rôle modifié : les ${team.usages.length} désignations de l’équipe seront à revoir.`);
      }
      team.usages
        .filter(usage => !rules.support.clients.includes(usage.establishment))
        .forEach(usage =>
          warnings.push(
            `${usage.designation} sur ${usage.establishment.name} : hors du périmètre, la désignation reste signalée.`,
          ),
        );
    }
    return warnings;
  }
}
